import { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { jsPDF } from "jspdf";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { X, FileText, Pen, Check, RotateCcw, Percent } from "lucide-react";
import logoSrc from "../assets/logo.png";

const AGREEMENT_TEXT = `
ADVANTA PLUS PARTNER AGREEMENT

This Partner Agreement ("Agreement") is entered into as of the date of electronic signature below, by and between Advanta Plus ("Company") and the undersigned business partner ("Partner").

1. PARTNERSHIP TERMS

1.1 The Partner agrees to offer the discount stated in the Agreed Discount box above (typically in the range of 10%–20%) to Advanta Plus members presenting a valid membership card or QR code.

1.2 The Partner shall verify membership validity using the official Advanta Plus QR scanner application before applying discounts.

1.3 The Partner agrees to display Advanta Plus promotional materials at their place of business.

2. OBLIGATIONS OF THE PARTNER

2.1 The Partner shall honor all valid Advanta Plus memberships without discrimination.

2.2 The Partner shall not charge any fees to Advanta Plus members for using their membership benefits.

2.3 The Partner shall maintain the quality of goods and services offered to Advanta Plus members at the same standard as regular customers.

2.4 The Partner shall notify Advanta Plus of any changes to their business operations, including closure, relocation, or significant changes to services offered.

2.5 The Partner acknowledges and agrees that a single Advanta Plus membership card shall entitle the cardholder and the entire group accompanying the cardholder to the agreed discount on a single bill, with no limit on the number of people in the group.

2.6 The Partner shall accept reservations and walk-ins from Advanta Plus members regardless of the size of the accompanying group, subject only to the venue's standard capacity limits and reservation policy as applied equally to non-member guests.

3. OBLIGATIONS OF ADVANTA PLUS

3.1 Advanta Plus shall list the Partner in the official Partner Directory available to all members.

3.2 Advanta Plus shall provide the Partner with access to the QR code verification system.

3.3 Advanta Plus shall promote the Partner through its marketing channels and member communications.

4. INTELLECTUAL PROPERTY

4.1 Neither party shall use the other's trademarks, logos, or branding without prior written consent.

4.2 Advanta Plus grants the Partner a limited, non-exclusive license to display Advanta Plus branding materials provided by the Company.

5. TERM AND TERMINATION

5.1 This Agreement shall commence on the date of signature and continue for an initial term of one (1) year.

5.2 Either party may terminate this Agreement with thirty (30) days written notice.

5.3 Advanta Plus reserves the right to terminate this Agreement immediately if the Partner fails to honor member discounts or engages in conduct detrimental to the Advanta Plus brand.

6. LIMITATION OF LIABILITY

6.1 Neither party shall be liable for any indirect, incidental, or consequential damages arising from this Agreement.

6.2 The Partner acknowledges that participation in the Advanta Plus program does not guarantee any specific number of customer visits or sales.

7. DATA PROTECTION

7.1 Both parties agree to comply with applicable data protection laws, including GDPR where applicable.

7.2 The Partner agrees not to collect, store, or process personal data of Advanta Plus members beyond what is necessary for the transaction.

8. GENERAL PROVISIONS

8.1 This Agreement constitutes the entire agreement between the parties regarding the subject matter hereof.

8.2 This Agreement shall be governed by the laws of Spain.

8.3 Any disputes arising from this Agreement shall be resolved through good faith negotiation, and if necessary, through the courts of Spain.

VERSIÓN EN ESPAÑOL

ACUERDO DE SOCIO ADVANTA PLUS

Este Acuerdo de Socio ("Acuerdo") se celebra a partir de la fecha de la firma electrónica a continuación, entre Advanta Plus ("Empresa") y el socio comercial firmante ("Socio").

1. TÉRMINOS DE LA ASOCIACIÓN

1.1 El Socio se compromete a ofrecer el descuento indicado en el recuadro «Descuento Acordado» anterior (normalmente entre el 10% y el 20%) a los miembros de Advanta Plus que presenten una tarjeta de membresía válida o código QR.

1.2 El Socio deberá verificar la validez de la membresía utilizando la aplicación oficial de escáner QR de Advanta Plus antes de aplicar los descuentos.

1.3 El Socio se compromete a exhibir los materiales promocionales de Advanta Plus en su establecimiento.

2. OBLIGACIONES DEL SOCIO

2.1 El Socio deberá aceptar todas las membresías válidas de Advanta Plus sin discriminación.

2.2 El Socio no cobrará ninguna tarifa a los miembros de Advanta Plus por utilizar los beneficios de su membresía.

2.3 El Socio mantendrá la calidad de los bienes y servicios ofrecidos a los miembros de Advanta Plus al mismo nivel que para los clientes regulares.

2.4 El Socio notificará a Advanta Plus cualquier cambio en sus operaciones comerciales, incluyendo cierre, reubicación o cambios significativos en los servicios ofrecidos.

2.5 El Socio reconoce y acepta que una sola tarjeta de membresía Advanta Plus dará derecho al titular y a todo el grupo que le acompañe al descuento acordado sobre una única cuenta, sin límite en el número de personas del grupo.

2.6 El Socio aceptará reservas y entradas sin reserva de los miembros de Advanta Plus con independencia del tamaño del grupo acompañante, sujeto únicamente a los límites de aforo y la política de reservas habituales del establecimiento, aplicados por igual a los clientes no miembros.

3. OBLIGACIONES DE ADVANTA PLUS

3.1 Advanta Plus incluirá al Socio en el Directorio oficial de Socios disponible para todos los miembros.

3.2 Advanta Plus proporcionará al Socio acceso al sistema de verificación de códigos QR.

3.3 Advanta Plus promocionará al Socio a través de sus canales de marketing y comunicaciones con los miembros.

4. PROPIEDAD INTELECTUAL

4.1 Ninguna de las partes utilizará las marcas registradas, logotipos o imagen de marca de la otra parte sin consentimiento previo por escrito.

4.2 Advanta Plus concede al Socio una licencia limitada y no exclusiva para exhibir los materiales de marca de Advanta Plus proporcionados por la Empresa.

5. DURACIÓN Y TERMINACIÓN

5.1 Este Acuerdo comenzará en la fecha de la firma y continuará por un período inicial de un (1) año.

5.2 Cualquiera de las partes podrá rescindir este Acuerdo con treinta (30) días de preaviso por escrito.

5.3 Advanta Plus se reserva el derecho de rescindir este Acuerdo de forma inmediata si el Socio no cumple con los descuentos para los miembros o incurre en conductas perjudiciales para la marca Advanta Plus.

6. LIMITACIÓN DE RESPONSABILIDAD

6.1 Ninguna de las partes será responsable de daños indirectos, incidentales o consecuentes derivados de este Acuerdo.

6.2 El Socio reconoce que la participación en el programa Advanta Plus no garantiza un número específico de visitas de clientes o ventas.

7. PROTECCIÓN DE DATOS

7.1 Ambas partes se comprometen a cumplir con las leyes de protección de datos aplicables, incluyendo el RGPD cuando corresponda.

7.2 El Socio se compromete a no recopilar, almacenar ni procesar datos personales de los miembros de Advanta Plus más allá de lo necesario para la transacción.

8. DISPOSICIONES GENERALES

8.1 Este Acuerdo constituye el acuerdo completo entre las partes en relación con el objeto del mismo.

8.2 Este Acuerdo se regirá por las leyes de España.

8.3 Cualquier disputa derivada de este Acuerdo se resolverá mediante negociación de buena fe y, si fuera necesario, ante los tribunales de España.

By signing below, the Partner acknowledges that they have read, understood, and agree to be bound by the terms and conditions of this Agreement in both English and Spanish versions.

Al firmar a continuación, el Socio reconoce que ha leído, comprendido y acepta estar vinculado por los términos y condiciones de este Acuerdo en sus versiones en inglés y español.
`;

const PartnerAgreement = ({ businessName, contactName, contactPosition, onSign, onSaveDraft, onCancel }) => {
  const [agreed, setAgreed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const sigCanvas = useRef(null);

  const discountValid = discountPercent !== "" && Number(discountPercent) > 0 && Number(discountPercent) <= 100;

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setSigned(false);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setSigned(true);
    }
  };

  const generatePDF = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      return null;
    }

    setGenerating(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      // Logo at top — new wide Advanta Plus artwork (4:3 aspect)
      try {
        doc.addImage(logoSrc, "PNG", (pageWidth - 50) / 2, 8, 50, 37.5);
      } catch (e) {
        // logo load failure shouldn't block the PDF
      }

      // Title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("ADVANTA PLUS", pageWidth / 2, 52, { align: "center" });
      doc.setFontSize(14);
      doc.text("PARTNER AGREEMENT", pageWidth / 2, 60, { align: "center" });

      // Date
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      doc.text(`Date: ${currentDate}`, margin, 72);

      // Partner Info
      doc.text(`Business Name: ${businessName}`, margin, 80);
      doc.text(`Representative: ${contactName}${contactPosition ? ` (${contactPosition})` : ''}`, margin, 88);

      // Agreed Discount box — fills with values the partner entered before signing
      const boxY = 94;
      const boxH = 17;
      doc.setDrawColor(212, 175, 55);  // gold
      doc.setFillColor(255, 251, 239); // light cream
      doc.setLineWidth(0.6);
      doc.roundedRect(margin, boxY, contentWidth, boxH, 2, 2, "FD");
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(184, 134, 11);
      doc.text("AGREED DISCOUNT", margin + 4, boxY + 6);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(`Discount offered to members: ${discountPercent}%`, margin + 4, boxY + 13);

      // Agreement text
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(AGREEMENT_TEXT.trim(), contentWidth);

      let yPosition = boxY + boxH + 8;
      const lineHeight = 4.5;
      const pageHeight = doc.internal.pageSize.getHeight();
      
      for (let i = 0; i < lines.length; i++) {
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(lines[i], margin, yPosition);
        yPosition += lineHeight;
      }
      
      // Signature section on the last page
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = 20;
      }
      
      yPosition += 10;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("SIGNATURE", margin, yPosition);
      
      yPosition += 8;
      doc.setFont("helvetica", "normal");
      doc.text(`Representative: ${contactName}${contactPosition ? ` (${contactPosition})` : ''}`, margin, yPosition);
      
      yPosition += 6;
      doc.text(`Business: ${businessName}`, margin, yPosition);
      
      yPosition += 6;
      doc.text(`Date Signed: ${currentDate}`, margin, yPosition);
      
      yPosition += 6;
      doc.text(`Time: ${new Date().toLocaleTimeString("en-GB")}`, margin, yPosition);
      
      // Add signature image
      yPosition += 10;
      const signatureData = sigCanvas.current.toDataURL("image/png");
      doc.addImage(signatureData, "PNG", margin, yPosition, 60, 30);
      
      // Add signature line
      doc.setDrawColor(0);
      doc.line(margin, yPosition + 32, margin + 80, yPosition + 32);
      doc.setFontSize(8);
      doc.text("Authorized Signature", margin, yPosition + 38);
      
      // Footer with document ID
      const docId = `DOC-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      doc.setFontSize(7);
      doc.setTextColor(128);
      doc.text(`Document ID: ${docId}`, margin, pageHeight - 10);
      doc.text("This document was electronically signed via Advanta Plus Partner Portal", margin, pageHeight - 6);
      
      // Convert to blob
      const pdfBlob = doc.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const discountInfo = () => ({
    discount_percent: discountPercent ? Number(discountPercent) : null,
  });

  const handleSubmit = async () => {
    const pdfBlob = await generatePDF();
    if (pdfBlob) {
      onSign(pdfBlob, discountInfo());
    }
  };

  const handleSaveDraftClick = async () => {
    const pdfBlob = await generatePDF();
    if (pdfBlob && onSaveDraft) {
      onSaveDraft(pdfBlob, discountInfo());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0B0F19] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-[#D4AF37]/20 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-['Playfair_Display'] text-xl text-white">Partner Agreement</h2>
              <p className="text-white/50 text-sm">Please review and sign to continue</p>
            </div>
          </div>
          <button 
            onClick={onCancel}
            className="text-white/50 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Agreement Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Agreed Discount section — partner enters their specific commercial offer here */}
          <div className="mb-6 p-5 rounded-xl border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5">
            <div className="flex items-center gap-2 mb-3">
              <Percent className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="text-[#D4AF37] font-semibold uppercase tracking-wider text-sm">Agreed Discount</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="discount_pct" className="text-white/70 text-xs mb-1 block">Discount % *</Label>
                <Input
                  id="discount_pct" type="number" min="1" max="100" step="0.5"
                  placeholder="e.g. 15"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  className="bg-[#0B0F19] border-white/20 text-white"
                />
              </div>
            </div>
            <p className="text-white/50 text-xs mt-3 italic">This is the discount your business commits to. It will be printed in the signed agreement and shown to members.</p>
          </div>

          <div className="bg-white/5 rounded-xl p-6 mb-6">
            <pre className="text-white/80 text-sm whitespace-pre-wrap font-sans leading-relaxed">
              {AGREEMENT_TEXT}
            </pre>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start gap-3 mb-6 p-4 bg-[#D4AF37]/5 rounded-xl border border-[#D4AF37]/20">
            <Checkbox
              id="agree"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
            />
            <Label htmlFor="agree" className="text-white/80 text-sm leading-relaxed cursor-pointer">
              I, <strong className="text-[#D4AF37]">{contactName}</strong>{contactPosition ? <>, <strong className="text-[#D4AF37]">{contactPosition}</strong></> : ''} of <strong className="text-[#D4AF37]">{businessName}</strong>,
              have read and agree to the terms and conditions of this Partner Agreement.
            </Label>
          </div>

          {/* Signature Section */}
          {agreed && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-white">
                <Pen className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-medium">Your Signature</span>
              </div>
              
              <div className="relative bg-white rounded-xl overflow-hidden">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: "w-full h-40",
                    style: { width: "100%", height: "160px" }
                  }}
                  backgroundColor="white"
                  penColor="black"
                  onEnd={handleSignatureEnd}
                />
                {!signed && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-gray-400 text-sm">Draw your signature here</span>
                  </div>
                )}
              </div>
              
              <button
                type="button"
                onClick={clearSignature}
                className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Clear signature
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex flex-wrap justify-end gap-3 flex-shrink-0">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-white/70 hover:text-white"
          >
            Cancel
          </Button>
          {onSaveDraft && (
            <Button
              variant="ghost"
              onClick={handleSaveDraftClick}
              disabled={!agreed || !signed || generating || !discountValid}
              className="text-white/80 hover:text-white border border-white/20 hover:border-[#D4AF37]/40"
              title="Save signed agreement now and finish the application details later"
            >
              Save as Draft
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!agreed || !signed || generating || !discountValid}
            className="btn-gold px-8"
          >
            {generating ? (
              "Generating..."
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Sign & Submit
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PartnerAgreement;
